import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  AUTH_INITIAL_CLEANUP_STATE,
  AUTH_INITIAL_PRE_AUTH_ATTEMPT_STATE,
  AUTH_INITIAL_RENEWAL_STATE,
  AUTH_INITIAL_SESSION_STATE,
  AUTH_SLICE_NAME,
} from './constants';
// NOTE: explicit `.ts` extension — see constants.ts for why (`types.js` collision).
import {
  AuthState,
  CleanupTransactionStatus,
  DashboardSessionStatus,
  PreAuthAttemptStatus,
  RenewalEpisodeStatus,
  SetCleanupStatusPayload,
  SetPreAuthAttemptDecisionPayload,
  SetPreAuthAttemptStatusPayload,
  SetRenewalStatusPayload,
  SetSessionStatusPayload,
} from './types.ts';

/**
 * Non-secret authentication lifecycle Redux slice (T014).
 *
 * No secret/token value is ever stored here (constitution; data-model.md §6). Every mutation of
 * `preAuthAttempt` or `session` that targets a specific attempt/session is generation-guarded: a
 * payload carrying a stale `generation` is silently ignored so only the latest generation may
 * change state (data-model.md §3 "a newer generation invalidates older completions"; §7
 * "[session generation is] invalidated on logout, unauthorized cleanup, environment change, or
 * replacement").
 */
const initialState: AuthState = {
  preAuthAttempt: AUTH_INITIAL_PRE_AUTH_ATTEMPT_STATE,
  session: AUTH_INITIAL_SESSION_STATE,
  renewal: AUTH_INITIAL_RENEWAL_STATE,
  cleanup: AUTH_INITIAL_CLEANUP_STATE,
};

export const authSlice = createSlice({
  name: AUTH_SLICE_NAME,
  initialState,
  reducers: {
    /** Starts a new latest-generation pre-authentication attempt, superseding any prior one. */
    beginPreAuthAttempt: (state) => {
      state.preAuthAttempt.generation += 1;
      state.preAuthAttempt.status = PreAuthAttemptStatus.VALIDATING;
      state.preAuthAttempt.decision = null;
    },
    /** Advances the current attempt's status; ignored if `generation` is stale. */
    setPreAuthAttemptStatus: (state, action: PayloadAction<SetPreAuthAttemptStatusPayload>) => {
      if (action.payload.generation !== state.preAuthAttempt.generation) {
        return;
      }
      state.preAuthAttempt.status = action.payload.status;
    },
    /** Records the authoritative route decision for the current attempt; ignored if stale. */
    setPreAuthAttemptDecision: (state, action: PayloadAction<SetPreAuthAttemptDecisionPayload>) => {
      if (action.payload.generation !== state.preAuthAttempt.generation) {
        return;
      }
      state.preAuthAttempt.decision = action.payload.decision;
      state.preAuthAttempt.status = PreAuthAttemptStatus.DECIDED;
    },
    /** Returns the pre-authentication attempt to a fresh idle generation (e.g. new submission). */
    resetPreAuthAttempt: (state) => {
      state.preAuthAttempt.generation += 1;
      state.preAuthAttempt.status = PreAuthAttemptStatus.IDLE;
      state.preAuthAttempt.decision = null;
    },
    /** Starts a new latest-generation session lifecycle (e.g. sign-in begins). */
    beginSession: (state) => {
      state.session.generation += 1;
      state.session.status = DashboardSessionStatus.AUTHENTICATING;
    },
    /** Advances the current session's status; ignored if `generation` is stale. */
    setSessionStatus: (state, action: PayloadAction<SetSessionStatusPayload>) => {
      if (action.payload.generation !== state.session.generation) {
        return;
      }
      state.session.status = action.payload.status;
    },
    /**
     * Invalidates the current session generation and clears non-secret lifecycle state on
     * logout or unauthorized cleanup (data-model.md §7, §9). Secret/token cleanup itself is
     * owned by the auth boundary (T017), never Redux.
     */
    invalidateSession: (state) => {
      state.session.generation += 1;
      state.session.status = DashboardSessionStatus.SIGNED_OUT;
      state.renewal.status = RenewalEpisodeStatus.IDLE;
      state.cleanup.status = CleanupTransactionStatus.IDLE;
    },
    /** Sets the current single-flight renewal episode status (data-model.md §8). */
    setRenewalStatus: (state, action: PayloadAction<SetRenewalStatusPayload>) => {
      state.renewal.status = action.payload.status;
    },
    /** Sets the current single-flight cleanup transaction status (data-model.md §9). */
    setCleanupStatus: (state, action: PayloadAction<SetCleanupStatusPayload>) => {
      state.cleanup.status = action.payload.status;
    },
    /** Hard reset to initial state (e.g. test isolation or environment change). */
    resetAuthState: () => initialState,
  },
});

export const {
  beginPreAuthAttempt,
  setPreAuthAttemptStatus,
  setPreAuthAttemptDecision,
  resetPreAuthAttempt,
  beginSession,
  setSessionStatus,
  invalidateSession,
  setRenewalStatus,
  setCleanupStatus,
  resetAuthState,
} = authSlice.actions;

export const { reducer: authReducer } = authSlice;
